/**
 * Contexto do CCO: dados ao vivo, cadastro (dispositivo → ativo e ficha) e alarmes agrupados — Nexora Grid (US-402).
 *
 * Criado em: 16/10/2026 · Última modificação: 01/11/2026 · Versão: 1.2.0
 * v1.2 (Sprint 44): expõe `ativos` (cadastro inteiro) para o Mapa de Comando mostrar o que ainda não tem telemetria.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../api";
import type { Ativo } from "../cadastro/tipos";
import { useSessao } from "../sessao";
import type { Prioridade } from "./medidas";
import { useTempoReal, type EstadoTempoReal } from "./useTempoReal";

export type GrupoAlarme = {
  device_id: string; unidade: string; tipo: string; severidade: string; prioridade: Prioridade; ocorrencias: number;
  primeiro: string; ultimo: string; descricao: string; requer_intervencao: boolean; ativos: string[];
  reconhecido?: boolean; reconhecido_por?: string | null; reconhecido_em?: string | null;
};
type ContextoCco = {
  empresa: string | null;
  vivo: EstadoTempoReal;
  ativosPorDevice: Map<string, Ativo[]>;
  /** Cadastro de ativos da operadora (inclui os que ainda não têm telemetria). */
  ativos: Ativo[];
  alarmes: GrupoAlarme[];
  contagem: Record<string, number>;
  recarregarAlarmes: () => void;
};

const Contexto = createContext<ContextoCco | null>(null);

export function ProvedorCco({ children }: { children: ReactNode }) {
  const { empresa } = useSessao();
  const vivo = useTempoReal(empresa);
  const [ativos, setAtivos] = useState<Ativo[]>([]);
  const [alarmes, setAlarmes] = useState<GrupoAlarme[]>([]);
  const [contagem, setContagem] = useState<Record<string, number>>({});

  useEffect(() => {
    if (empresa) api<Ativo[]>(`/v1/ativos?empresa=${empresa}`).then(setAtivos).catch(() => setAtivos([]));
  }, [empresa]);

  const recarregarAlarmes = useCallback(() => {
    if (!empresa) return;
    api<{ grupos: GrupoAlarme[]; contagem_por_prioridade: Record<string, number> }>(`/v1/alarmes?empresa=${empresa}&horas=24`)
      .catch(() => api<{ grupos: GrupoAlarme[]; contagem_por_prioridade: Record<string, number> }>(
        `/v1/tempo-real/alarmes?empresa=${empresa}&horas=24`))
      .then((r) => { setAlarmes(r.grupos); setContagem(r.contagem_por_prioridade); })
      .catch(() => undefined);
  }, [empresa]);

  useEffect(() => {
    recarregarAlarmes();
    const relogio = window.setInterval(recarregarAlarmes, 20000);
    return () => window.clearInterval(relogio);
  }, [recarregarAlarmes]);
  // alarme novo pelo fluxo: atualiza a lista agrupada na hora
  useEffect(() => { if (vivo.alarmesNovos.length) recarregarAlarmes(); }, [vivo.alarmesNovos.length, recarregarAlarmes]);

  const ativosPorDevice = useMemo(() => {
    const mapa = new Map<string, Ativo[]>();
    ativos.forEach((a) => { if (a.dados.device_id) mapa.set(a.dados.device_id, [...(mapa.get(a.dados.device_id) ?? []), a]); });
    return mapa;
  }, [ativos]);

  const valor = useMemo(() => ({ empresa, vivo, ativosPorDevice, ativos, alarmes, contagem, recarregarAlarmes }),
    [empresa, vivo, ativosPorDevice, ativos, alarmes, contagem, recarregarAlarmes]);
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useCco(): ContextoCco {
  const c = useContext(Contexto);
  if (!c) throw new Error("useCco fora do ProvedorCco");
  return c;
}

/** Corrente nominal da ficha do ativo ligado ao dispositivo (para a faixa da corrente). */
export function correnteNominal(ativos: Ativo[] | undefined): number | undefined {
  const v = ativos?.map((a) => Number(a.ficha?.corrente_nominal_a)).find((n) => n > 0);
  return v || undefined;
}

/** Tensão nominal da ficha (`tensao_v`); sem ficha, o nível da medição (220 V ou 380 V). */
export function tensaoNominal(ativos: Ativo[] | undefined, medida?: number): number | undefined {
  const v = ativos?.map((a) => Number(a.ficha?.tensao_v)).find((n) => n > 0);
  return v || (medida !== undefined ? (medida > 300 ? 380 : 220) : undefined);
}
